import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import * as React from "react";
import { cx } from "../internal/cx.js";

export type AccordionVariant = "separated" | "flush";

/**
 * Accordion — stack of expandable disclosure rows.
 *
 * Built on Radix Accordion so we get full keyboard support (Home/End,
 * ArrowDown/Up) and ARIA wiring for free. Two visual variants:
 *   - 'separated' (default): each item is its own card with a subtle border.
 *     Use inside content areas where the accordion floats on the page.
 *   - 'flush': items share a single bordered list (no per-item card).
 *     Use inside surfaces that already have their own card (settings panes,
 *     sheets, dialogs).
 *
 * Type prop is passed straight to Radix:
 *   type="single" — only one item open at a time. Pass `collapsible` to
 *                   allow closing the open item (otherwise one is always open).
 *   type="multiple" — any number of items open simultaneously.
 */

export interface AccordionProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>,
    "value" | "defaultValue" | "onValueChange" | "type"
  > {
  variant?: AccordionVariant;
}

type AccordionRootProps = React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>;

/**
 * Root accordion. Forwards every Radix prop (including the `type` /
 * `value` / `defaultValue` discriminated union) so single + multiple modes
 * both type-check cleanly.
 */
export const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  AccordionRootProps & { variant?: AccordionVariant }
>(function Accordion({ className, variant = "separated", ...props }, ref) {
  return (
    <AccordionPrimitive.Root
      ref={ref}
      className={cx("cw-v2-accordion", `cw-v2-accordion--${variant}`, className)}
      {...(props as AccordionRootProps)}
    />
  );
});
Accordion.displayName = "Accordion";

export const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(function AccordionItem({ className, ...props }, ref) {
  return (
    <AccordionPrimitive.Item
      ref={ref}
      className={cx("cw-v2-accordion__item", className)}
      {...props}
    />
  );
});
AccordionItem.displayName = "AccordionItem";

export interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  /** Override the chevron icon. Pass `null` to hide it entirely. */
  icon?: React.ReactNode;
}

/**
 * Clickable header. Always wrapped in an Accordion.Header by Radix; the
 * chevron rotates 180° via data-state="open" / "closed".
 */
export const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(function AccordionTrigger({ children, className, icon, ...props }, ref) {
  return (
    <AccordionPrimitive.Header className="cw-v2-accordion__header">
      <AccordionPrimitive.Trigger
        ref={ref}
        className={cx("cw-v2-accordion__trigger", className)}
        {...props}
      >
        <span className="cw-v2-accordion__trigger-label">{children}</span>
        {icon === undefined ? (
          <ChevronDown aria-hidden className="cw-v2-accordion__chevron" />
        ) : (
          icon
        )}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
});
AccordionTrigger.displayName = "AccordionTrigger";

/**
 * Expanding region. Radix exposes a CSS custom property
 * `--radix-accordion-content-height` we use to animate from 0 to the
 * measured height (smooth slide).
 */
export const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(function AccordionContent({ children, className, ...props }, ref) {
  return (
    <AccordionPrimitive.Content
      ref={ref}
      className={cx("cw-v2-accordion__content", className)}
      {...props}
    >
      <div className="cw-v2-accordion__content-inner">{children}</div>
    </AccordionPrimitive.Content>
  );
});
AccordionContent.displayName = "AccordionContent";
